import React, { useMemo } from 'react';

import type { Rule } from 'antd/lib/form';
import { useTranslation } from 'react-i18next';
import { TypographyTitle, InputNumber, FormItem } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { useHasFeatureFlag } from '@bringg-frontend/hooks';
import { InfoTooltip } from '@bringg-frontend/ui';

import HandlingUnitsInput from './components/handling-units-input';
import HandlingUnitsListInput from './components/handling-units-list-input';
import { HANDLING_UNITS_EDITOR_FF, MIN_NUMBER_VALUE } from './constants';

const CapacityRestrictionsSection = () => {
	const { t } = useTranslation();
	const handlingUnitsEditorEnabled = useHasFeatureFlag(HANDLING_UNITS_EDITOR_FF);
	const inputNumberRules = useMemo<Rule[]>(
		() => [
			{ type: 'number', min: MIN_NUMBER_VALUE, message: t('VEHICLE_PROFILES.ERROR.NUMBER_INPUT.MIN_VALUE') },
			{ type: 'integer', message: t('VEHICLE_PROFILES.ERROR.NUMBER_INPUT.INTEGER') }
		],
		[t]
	);

	return (
		<div className="vehicle-profiles-form--section-wrapper">
			<div>
				<TypographyTitle level={4} className="vehicle-profiles-form--section-icon">
					<BringgIcon iconName={BringgFontIcons.Weight} />
				</TypographyTitle>
			</div>
			<div className="vehicle-profiles-form--section-content">
				<TypographyTitle level={4} className="vehicle-profiles-form--section-title">
					{t('VEHICLE_PROFILES.FORM.CAPACITY_RESTRICTIONS')}
				</TypographyTitle>
				<div className="vehicle-profiles-form--section-sub-title">
					{t('VEHICLE_PROFILES.FORM.CAPACITY_RESTRICTIONS_DESCRIPTION')}
				</div>
				<FormItem
					name="handling_units"
					className="vehicle-profiles-form-item"
					noStyle={handlingUnitsEditorEnabled}
				>
					{handlingUnitsEditorEnabled ? <HandlingUnitsListInput /> : <HandlingUnitsInput />}
				</FormItem>
				<div className="vehicle-profiles-form--flex-group">
					<div className="vehicle-profiles-form--input-group">
						<div className="vehicle-profiles-form--label-text">
							{t('VEHICLE_PROFILES.FORM.MAX_TOTAL_WEIGHT')}
						</div>
						<FormItem
							name="max_total_weight"
							rules={inputNumberRules}
							className="vehicle-profiles-form-item"
						>
							<InputNumber
								placeholder={t('VEHICLE_PROFILES.FORM.WEIGHT_CAPACITY_PLACEHOLDER')}
								className="vehicle-profiles-form--input"
								data-test-id="max_total_weight"
							/>
						</FormItem>
					</div>
					<div className="vehicle-profiles-form--input-group">
						<div className="vehicle-profiles-form--label-text">
							{t('VEHICLE_PROFILES.FORM.MAXIMUM_TASKS_PER_ROUTE')}
							<InfoTooltip
								title={t('VEHICLE_PROFILES.FORM.HELP.MAXIMUM_TASKS_PER_ROUTE')}
								placement="right"
							/>
						</div>
						<FormItem
							name="maximum_tasks_per_route"
							rules={inputNumberRules}
							className="vehicle-profiles-form-item"
						>
							<InputNumber
								placeholder={t('VEHICLE_PROFILES.FORM.MAXIMUM_TASKS_PER_ROUTE_PLACEHOLDER')}
								className="vehicle-profiles-form--input"
								data-test-id="maximum_tasks_per_route"
							/>
						</FormItem>
					</div>
				</div>

				<div className="vehicle-profiles-form--flex-group">
					<div className="vehicle-profiles-form--group-title">
						{t('VEHICLE_PROFILES.FORM.DIMENSIONS_TITLE')}
						<InfoTooltip title={t('VEHICLE_PROFILES.FORM.HELP.DIMENSIONS')} placement="right" />
					</div>
					<div className="vehicle-profiles-form--input-group">
						<div className="vehicle-profiles-form--label-text">{t('VEHICLE_PROFILES.FORM.HEIGHT')}</div>
						<FormItem name="height" rules={inputNumberRules} className="vehicle-profiles-form-item">
							<InputNumber
								placeholder={t('VEHICLE_PROFILES.FORM.HEIGHT_PLACEHOLDER')}
								className="vehicle-profiles-form--input"
								data-test-id="container-height"
							/>
						</FormItem>
					</div>

					<div className="vehicle-profiles-form--input-group">
						<div className="vehicle-profiles-form--label-text">{t('VEHICLE_PROFILES.FORM.WIDTH')}</div>
						<FormItem name="width" rules={inputNumberRules} className="vehicle-profiles-form-item">
							<InputNumber
								placeholder={t('VEHICLE_PROFILES.FORM.WIDTH_PLACEHOLDER')}
								className="vehicle-profiles-form--input"
								data-test-id="container-width"
							/>
						</FormItem>
					</div>
					<div className="vehicle-profiles-form--input-group">
						<div className="vehicle-profiles-form--label-text">{t('VEHICLE_PROFILES.FORM.LENGTH')}</div>
						<FormItem name="length" rules={inputNumberRules} className="vehicle-profiles-form-item">
							<InputNumber
								placeholder={t('VEHICLE_PROFILES.FORM.LENGTH_PLACEHOLDER')}
								className="vehicle-profiles-form--input"
								data-test-id="container-length"
							/>
						</FormItem>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CapacityRestrictionsSection;
